import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import {
  DEFAULT_WIZARD_SPOTLIGHT_PADDING,
  WIZARD_STEP_REVEAL_DELAY_MS,
} from './constants';
import { getStepCardLayout } from './stepCardLayout';
import { areSpotlightShapesEqual, getSpotlightShape } from './spotlightShape';
import {
  getWizardAlignRect,
  queryWizardTarget,
  resolveWizardAlignTarget,
} from './targetUtils';
import type { SpotlightShape } from './types';
import { useWizardInteractionLock } from './useWizardInteractionLock';
import WizardOverlay from './WizardOverlay';
import { useWizardOptional } from './WizardProvider';
import WizardStepCard from './WizardStepCard';
import { useWizardsAllowed } from './wizardViewport';

function areCardPositionsEqual(
  a: { top: number; left: number },
  b: { top: number; left: number },
): boolean {
  return a.top === b.top && a.left === b.left;
}

export default function WizardHost() {
  const wizard = useWizardOptional();
  const cardRef = useRef<HTMLDivElement>(null);
  const [spotlightShape, setSpotlightShape] = useState<SpotlightShape | null>(
    null,
  );
  const [cardLayout, setCardLayout] = useState({ top: 0, left: 0 });
  const [stepSpotlightRevealed, setStepSpotlightRevealed] = useState(false);
  const [stepCardRevealed, setStepCardRevealed] = useState(false);
  const initialStepRevealStaggerDoneRef = useRef(false);

  const activeWizard = wizard?.activeWizard ?? null;
  const stepIndex = wizard?.stepIndex ?? 0;
  const currentStep = activeWizard?.steps[stepIndex] ?? null;
  const currentStepId = currentStep?.id ?? null;
  const wizardsAllowed = useWizardsAllowed();

  useWizardInteractionLock(Boolean(activeWizard) && wizardsAllowed, cardRef);

  useEffect(() => {
    initialStepRevealStaggerDoneRef.current = false;
  }, [activeWizard?.id]);

  useLayoutEffect(() => {
    if (!currentStepId) {
      setStepSpotlightRevealed(false);
      setStepCardRevealed(false);
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    const shouldStaggerInitialStep =
      stepIndex === 0 && !initialStepRevealStaggerDoneRef.current;

    if (prefersReducedMotion || !shouldStaggerInitialStep) {
      setStepSpotlightRevealed(true);
      setStepCardRevealed(true);
      if (stepIndex === 0) {
        initialStepRevealStaggerDoneRef.current = true;
      }
      return;
    }

    setStepSpotlightRevealed(false);
    setStepCardRevealed(false);

    const spotlightTimeoutId = window.setTimeout(() => {
      setStepSpotlightRevealed(true);
    }, WIZARD_STEP_REVEAL_DELAY_MS);

    const cardTimeoutId = window.setTimeout(() => {
      setStepCardRevealed(true);
      initialStepRevealStaggerDoneRef.current = true;
    }, WIZARD_STEP_REVEAL_DELAY_MS * 2);

    return () => {
      window.clearTimeout(spotlightTimeoutId);
      window.clearTimeout(cardTimeoutId);
    };
  }, [activeWizard?.id, currentStepId, stepIndex]);

  const updateLayout = useCallback(() => {
    if (!currentStep) {
      setSpotlightShape(null);
      return;
    }

    const target = queryWizardTarget(currentStep.targetId);
    if (!target) {
      return;
    }

    const padding =
      currentStep.spotlightPadding ?? DEFAULT_WIZARD_SPOTLIGHT_PADDING;
    const nextSpotlightShape = getSpotlightShape(
      target,
      padding,
      currentStep.spotlightInset ?? 0,
    );
    setSpotlightShape((previousShape) =>
      previousShape &&
      areSpotlightShapesEqual(previousShape, nextSpotlightShape)
        ? previousShape
        : nextSpotlightShape,
    );

    const cardElement = cardRef.current;
    if (!cardElement) {
      return;
    }

    const alignTarget = resolveWizardAlignTarget(currentStep, target);
    const align = alignTarget ? getWizardAlignRect(alignTarget) : undefined;

    const nextCardLayout = getStepCardLayout({
      placement: currentStep.placement ?? 'top',
      pointerAnchor: currentStep.pointerAnchor,
      spotlight: nextSpotlightShape,
      cardWidth: cardElement.offsetWidth,
      cardHeight: cardElement.offsetHeight,
      align,
    });
    setCardLayout((previousLayout) =>
      areCardPositionsEqual(previousLayout, nextCardLayout)
        ? previousLayout
        : nextCardLayout,
    );
  }, [currentStep]);

  useLayoutEffect(() => {
    updateLayout();
  }, [updateLayout]);

  useEffect(() => {
    if (!currentStep) {
      return;
    }

    let animationFrameId = 0;
    const syncLayout = () => {
      updateLayout();
      animationFrameId = window.requestAnimationFrame(syncLayout);
    };

    animationFrameId = window.requestAnimationFrame(syncLayout);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [currentStep, updateLayout]);

  useEffect(() => {
    if (!currentStep) {
      return;
    }

    const target = queryWizardTarget(currentStep.targetId);
    if (!target) {
      return;
    }

    const alignTarget = resolveWizardAlignTarget(currentStep, target);

    const resizeObserver = new ResizeObserver(() => {
      updateLayout();
    });
    resizeObserver.observe(target);
    if (alignTarget) {
      resizeObserver.observe(alignTarget);
    }
    resizeObserver.observe(document.documentElement);

    let ancestor: HTMLElement | null = target.parentElement;
    while (ancestor) {
      resizeObserver.observe(ancestor);
      ancestor = ancestor.parentElement;
    }

    const handleViewportChange = () => {
      updateLayout();
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    window.visualViewport?.addEventListener('resize', handleViewportChange);
    window.visualViewport?.addEventListener('scroll', handleViewportChange);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
      window.visualViewport?.removeEventListener('resize', handleViewportChange);
      window.visualViewport?.removeEventListener('scroll', handleViewportChange);
    };
  }, [currentStep, updateLayout]);

  useEffect(() => {
    if (!activeWizard) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        wizard?.endWizard('skipped');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeWizard, wizard]);

  if (
    !wizard ||
    !activeWizard ||
    !currentStep ||
    !wizardsAllowed ||
    typeof document === 'undefined'
  ) {
    return null;
  }

  const placement = currentStep.placement ?? 'top';

  return createPortal(
    <>
      {spotlightShape && stepSpotlightRevealed ? (
        <WizardOverlay spotlightShape={spotlightShape} />
      ) : null}
      <WizardStepCard
        key={currentStep.id}
        title={currentStep.title}
        body={currentStep.body}
        stepIndex={stepIndex}
        totalSteps={activeWizard.steps.length}
        placement={placement}
        pointerAnchor={currentStep.pointerAnchor}
        style={{ top: cardLayout.top, left: cardLayout.left }}
        cardRef={cardRef}
        revealed={stepCardRevealed}
        onBack={() => wizard.goToPreviousStep()}
        onDone={() => wizard.endWizard('completed')}
        onEndTour={() => wizard.endWizard('ended')}
        onNext={() => wizard.goToNextStep()}
      />
    </>,
    document.body,
  );
}
